/**
 * 조건부 서식(CF) 공유 변환 모델 — 순수 모듈(렌더러 미참조).
 *
 * 렌더러(ExcelRenderer)가 파싱해 보관하는 상태(`CfBlock`: 0-based rect 목록 +
 * dxfId 가 스타일로 해소된 `CfRuleParsed`)와 workbook model 엔트리
 * (`{ range, rule }`, dxf 는 bgColor/fontColor/bold/italic 필드명) 사이의
 * **무손실** 상호 변환을 담당한다. 저작 UI(CF 다이얼로그)와 앱 직렬화기
 * (OOXML serializer)가 dxf 필드 매핑을 이 모듈 하나로 공유한다.
 *
 * 무손실 원칙(전 타입 통과): cellIs/expression/colorScale 뿐 아니라
 * containsText/notContainsText/beginsWith/endsWith 등 업로드 파일 유래 규칙도
 * type/text/operator/formulas/priority 를 그대로 실어 나른다 — 저작 v1 enum
 * 제한은 저작 표면(다이얼로그·패치 검증·도구 스키마)에서만 적용하고, 스냅샷
 * 방출 경로는 여기서 전량 통과시켜 "첫 CF 편집이 업로드 규칙을 통삭제"하는
 * 함정을 막는다.
 *
 * 주의: xlsx-engine 소속 공유 모듈이므로 호스트 앱의 types.ts 를 import하지
 * 않는다(공유→앱 의존 금지). IR 엔트리는 아래 로컬 구조 타입으로 동형
 * 선언하며, 필드/의미를 1:1 로 유지할 것.
 */

import {
  type GridRect,
  a1FromRowCol,
  colLettersToIndex,
  parseA1Range,
} from '../algorithms/structuralShift';

/* ------------------------------------------------------------------ */
/* 타입 — 렌더러 측(파싱 결과) 형상                                     */
/* ------------------------------------------------------------------ */

/** 0-based inclusive 사각 범위(렌더러 ValidationRangeRect 와 구조 동형). */
export type CfRangeRect = GridRect;

/** 차등서식(dxf) — 렌더러가 CSS 로 쓰기 좋은 형태로 해소한 스타일. */
export interface CfDxf {
  backgroundColor?: string;
  color?: string;
  fontWeight?: string;
  fontStyle?: string;
}

/** 렌더러가 파싱한 CF 규칙 1개(ExcelRenderer 의 CfRuleParsed 와 구조 동형). */
export interface CfRuleParsed {
  type: string;
  priority: number;
  operator?: string;
  /** containsText 계열의 대상 문자열. */
  text?: string;
  /** `<formula>` 내용(앞 '=' 없음). */
  formulas: string[];
  /** dxf(cellIs/expression/text 계열). */
  dxf?: CfDxf;
  /** 2/3색 색조(컬러 스케일). */
  colorScale?: { cfvo: Array<{ type: string; val?: number }>; colors: string[] };
  /**
   * Original open IR rule. Renderer-known fields are projected separately for
   * evaluation/editing, while this sidecar preserves unsupported OOXML fields
   * through structural edits and snapshot serialization.
   */
  sourceRule?: CfIrRule;
}

/** sqref 1개에 대응하는 CF 블록(ExcelRenderer 의 CfBlock 과 구조 동형). */
export interface CfBlock {
  rects: CfRangeRect[];
  rules: CfRuleParsed[];
}

/* ------------------------------------------------------------------ */
/* 타입 — IR 측(직렬화기 입력) 형상                                     */
/* ------------------------------------------------------------------ */

/** IR dxf — 직렬화기(dxfToXml)가 소비하는 필드명. */
export interface CfIrDxf {
  [key: string]: unknown;
  bgColor?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
}

/** IR CF 규칙 — XlsxConditionalFormat.rule 과 구조 호환(개방형). */
export interface CfIrRule {
  [key: string]: unknown;
  type: string;
  operator?: string;
  text?: string;
  priority?: number;
  formula?: string[] | string;
  dxf?: CfIrDxf;
  colorScale?: { cfvo: Array<{ type: string; val?: string }>; colors: string[] };
}

/** IR CF 엔트리 — XlsxConditionalFormat 과 구조 호환. */
export interface CfIrEntry {
  range: string;
  rule: CfIrRule;
}

const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/* ------------------------------------------------------------------ */
/* A1 ↔ rect 헬퍼                                                      */
/* ------------------------------------------------------------------ */

/** xlsx 최대 행/열(0-based) — 전열("A:A")/전행("1:3") 참조 해석용. */
const XLSX_MAX_ROW = 1048575;
const XLSX_MAX_COL = 16383;

/** rect → A1 문자열. 단일 셀은 "B2", 범위는 "B2:D10". */
export const rectToA1 = (rect: CfRangeRect): string => {
  const start = a1FromRowCol(rect.minR, rect.minC);
  if (rect.minR === rect.maxR && rect.minC === rect.maxC) return start;
  return `${start}:${a1FromRowCol(rect.maxR, rect.maxC)}`;
};

/** A1 토큰 1개 → rect. 전열("A:C")/전행("2:5") 참조도 지원(파싱 불가 시 null). */
const a1TokenToRect = (token: string): CfRangeRect | null => {
  const t = token.trim();
  if (!t) return null;
  // 전열 참조: "A:A", "$A:$C"
  const colOnly = /^\$?([A-Za-z]{1,3}):\$?([A-Za-z]{1,3})$/.exec(t);
  if (colOnly) {
    const c1 = colLettersToIndex(colOnly[1]);
    const c2 = colLettersToIndex(colOnly[2]);
    return { minR: 0, maxR: XLSX_MAX_ROW, minC: Math.min(c1, c2), maxC: Math.max(c1, c2) };
  }
  // 전행 참조: "1:3"
  const rowOnly = /^\$?(\d+):\$?(\d+)$/.exec(t);
  if (rowOnly) {
    const r1 = parseInt(rowOnly[1], 10) - 1;
    const r2 = parseInt(rowOnly[2], 10) - 1;
    if (r1 < 0 || r2 < 0) return null;
    return { minR: Math.min(r1, r2), maxR: Math.max(r1, r2), minC: 0, maxC: XLSX_MAX_COL };
  }
  return parseA1Range(t);
};

/** sqref("B2 B5:B10") → rect 배열. 파싱 불가 토큰은 건너뛴다(fail-open). */
export const sqrefToRects = (sqref: string): CfRangeRect[] => {
  const rects: CfRangeRect[] = [];
  for (const token of sqref.trim().split(/\s+/)) {
    const r = a1TokenToRect(token);
    if (r) rects.push(r);
  }
  return rects;
};

/* ------------------------------------------------------------------ */
/* 규칙 단위 변환 (무손실 — dxf 필드 rename + cfvo val number↔string)    */
/* ------------------------------------------------------------------ */

/** 렌더러 dxf → IR dxf. backgroundColor→bgColor, color→fontColor,
 * fontWeight:'bold'→bold, fontStyle:'italic'→italic. */
export const cfDxfToIrDxf = (dxf: CfDxf): CfIrDxf => {
  const out: CfIrDxf = {};
  if (dxf.backgroundColor) out.bgColor = dxf.backgroundColor;
  if (dxf.color) out.fontColor = dxf.color;
  if (dxf.fontWeight === 'bold') out.bold = true;
  if (dxf.fontStyle === 'italic') out.italic = true;
  return out;
};

/** IR dxf → 렌더러 dxf(위 rename 의 역방향). */
export const irDxfToCfDxf = (dxf: CfIrDxf): CfDxf => {
  const out: CfDxf = {};
  if (dxf.bgColor) out.backgroundColor = dxf.bgColor;
  if (dxf.fontColor) out.color = dxf.fontColor;
  if (dxf.bold) out.fontWeight = 'bold';
  if (dxf.italic) out.fontStyle = 'italic';
  return out;
};

/** 렌더러 규칙 → IR 규칙. 전 타입 통과(type/text/operator/formulas/priority 보존).
 * priority 는 1 이상일 때만 명시로 실어 직렬화기의 backfill 과 충돌하지 않는다
 * (파서는 priority 누락을 0 으로 두므로 0 은 "미지정"으로 취급). */
export const cfRuleParsedToIrRule = (rule: CfRuleParsed): CfIrRule => {
  const out: CfIrRule = rule.sourceRule
    ? { ...cloneJson(rule.sourceRule), type: rule.type }
    : { type: rule.type };
  if (rule.operator) out.operator = rule.operator;
  if (rule.text != null) out.text = rule.text;
  if (typeof rule.priority === 'number' && rule.priority > 0) out.priority = rule.priority;
  if (rule.formulas.length > 0) out.formula = [...rule.formulas];
  if (rule.dxf) {
    const sourceDxf = out.dxf && typeof out.dxf === 'object' && !Array.isArray(out.dxf)
      ? out.dxf as Record<string, unknown>
      : {};
    out.dxf = { ...sourceDxf, ...cfDxfToIrDxf(rule.dxf) };
  }
  if (rule.colorScale) {
    const sourceColorScale = out.colorScale && typeof out.colorScale === 'object'
      ? out.colorScale as { cfvo?: unknown[]; [key: string]: unknown }
      : {};
    out.colorScale = {
      ...sourceColorScale,
      cfvo: rule.colorScale.cfvo.map((v, index) => ({
        ...(sourceColorScale.cfvo?.[index] && typeof sourceColorScale.cfvo[index] === 'object'
          ? sourceColorScale.cfvo[index] as Record<string, unknown>
          : {}),
        type: v.type,
        ...(v.val != null ? { val: String(v.val) } : {}),
      })),
      colors: [...rule.colorScale.colors],
    };
  }
  return out;
};

/** IR 규칙 → 렌더러 규칙(역방향). cfvo val 은 string→number 로 해석. */
export const irRuleToCfRuleParsed = (rule: CfIrRule): CfRuleParsed => {
  const legacyFormulas = rule.formulas;
  const formulas = Array.isArray(rule.formula)
    ? rule.formula.map(String)
    : typeof rule.formula === 'string'
      ? [rule.formula]
      : Array.isArray(legacyFormulas)
        ? legacyFormulas.map(String)
        : typeof legacyFormulas === 'string'
          ? [legacyFormulas]
          : [];
  const out: CfRuleParsed = {
    type: rule.type,
    priority: typeof rule.priority === 'number' && rule.priority > 0 ? rule.priority : 0,
    formulas,
    sourceRule: cloneJson(rule),
  };
  if (rule.operator) out.operator = rule.operator;
  if (rule.text != null) out.text = rule.text;
  if (rule.dxf) out.dxf = irDxfToCfDxf(rule.dxf);
  if (rule.colorScale) {
    out.colorScale = {
      cfvo: rule.colorScale.cfvo.map((v) => {
        const n = v.val != null ? parseFloat(v.val) : NaN;
        return { type: v.type, ...(Number.isFinite(n) ? { val: n } : {}) };
      }),
      colors: [...rule.colorScale.colors],
    };
  }
  return out;
};

/* ------------------------------------------------------------------ */
/* 블록 ↔ IR 엔트리                                                    */
/* ------------------------------------------------------------------ */

/**
 * 렌더러 CF 블록 목록 → IR 엔트리 목록(스냅샷 방출용).
 * 블록 1개(rects, rules[N]) 는 같은 range 문자열을 공유하는 엔트리 N 개가 된다
 * — 직렬화기(sheetConditionalFormattingXml)가 sqref 기준으로 재그룹하므로
 * `<conditionalFormatting>` 블록 구조가 보존된다.
 */
export const cfBlocksToIrEntries = (blocks: CfBlock[]): CfIrEntry[] => {
  const entries: CfIrEntry[] = [];
  for (const block of blocks) {
    const range = block.rects.map(rectToA1).join(' ');
    if (!range) continue;
    for (const rule of block.rules) {
      entries.push({ range, rule: cfRuleParsedToIrRule(rule) });
    }
  }
  return entries;
};

/**
 * IR 엔트리 1개 → 렌더러 CF 블록(규칙 1개). 다이얼로그가 파일 재파싱 없이
 * cfBlocks 상태에 규칙을 추가할 때 사용한다. range 파싱 불가 시 null.
 */
export const irEntryToCfBlock = (entry: CfIrEntry): CfBlock | null => {
  const rects = sqrefToRects(entry.range || '');
  if (rects.length === 0) return null;
  return { rects, rules: [irRuleToCfRuleParsed(entry.rule)] };
};
